import { useEffect, useRef } from "react";
import { useActionContext } from "~/actions/context";
import { shortcutActions } from "~/actions/definitions";
import type { Action, ActionContext } from "~/actions/types";
import { isMac } from "~/foundation/utils/platform";
import { toggleNavigation, useShellStore } from "~/shell/store";

/** Typing "d" in a search box must never repaint the app. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable ||
    target.getAttribute("role") === "textbox"
  );
}

/**
 * Does this keydown satisfy the action's declared binding? Bindings are written with the
 * logical "Meta" modifier and resolved per platform here, in one place.
 */
export function matchesShortcut(shortcut: readonly string[], event: KeyboardEvent): boolean {
  const keys = shortcut.map((key) => key.toLowerCase());
  const wantsMeta = keys.includes("meta") || keys.includes("mod");
  const wantsShift = keys.includes("shift");
  const wantsAlt = keys.includes("alt");
  const main = keys.find((key) => !["meta", "mod", "shift", "alt", "control"].includes(key));

  if (!main) {
    return false;
  }
  const primaryHeld = isMac() ? event.metaKey : event.ctrlKey;
  if (wantsMeta !== primaryHeld) {
    return false;
  }
  if (wantsAlt !== event.altKey) {
    return false;
  }
  if (wantsShift && !event.shiftKey) {
    return false;
  }
  // "?" is only reachable with Shift on most layouts, so an un-declared Shift is only
  // disqualifying for keys you can type without it.
  if (!wantsShift && event.shiftKey && /^[a-z0-9]$/.test(main)) {
    return false;
  }
  return event.key.toLowerCase() === main;
}

export function findShortcutAction(
  actions: readonly Action[],
  event: KeyboardEvent,
  context: ActionContext,
): Action | undefined {
  return actions.find((action) => {
    if (!action.shortcut || !action.perform) {
      return false;
    }
    if (action.visible && !action.visible(context)) {
      return false;
    }
    return matchesShortcut(action.shortcut, event);
  });
}

/**
 * One keydown listener for the whole app. The two shell-level bindings (⌘K, ⌘.) are handled
 * here because they move shell state; everything else is whatever the registry declares —
 * a shortcut is never written down twice.
 */
export function useGlobalShortcuts(): void {
  const context = useActionContext({ source: "shortcut" });
  const toggleCommandBar = useShellStore((state) => state.toggleCommandBar);

  // The context object is rebuilt every render; the listener must not be.
  const contextRef = useRef(context);
  contextRef.current = context;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const primaryHeld = isMac() ? event.metaKey : event.ctrlKey;

      if (primaryHeld && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggleCommandBar();
        return;
      }

      if (primaryHeld && event.key === ".") {
        event.preventDefault();
        toggleNavigation();
        return;
      }

      const current = contextRef.current;
      const action = findShortcutAction(shortcutActions(), event, current);
      // An action must opt in to firing from inside a text field; everything else stays out of
      // the way of somebody's sentence.
      if (action && (action.allowInInput || !isEditableTarget(event.target))) {
        event.preventDefault();
        void action.perform?.(current);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggleCommandBar]);
}
